import React, { useRef, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';

const CATEGORIES = {
  dev: {
    label: 'DEV',
    fullLabel: 'Software Development',
    color: '#3B82F6',
    bg: 'bg-blue-500',
    bgLight: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-600',
    badge: 'bg-blue-100 text-blue-700',
    jobs: ['Backend Developer', 'Frontend Developer', 'Full Stack Developer'],
  },
  data: {
    label: 'DATA / AI',
    fullLabel: 'Data & Artificial Intelligence',
    color: '#F59E0B',
    bg: 'bg-amber-500',
    bgLight: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-600',
    badge: 'bg-amber-100 text-amber-700',
    jobs: ['Data Analyst', 'Data Scientist', 'Data Engineer', 'Machine Learning Engineer', 'AI Engineer'],
  },
  cyber: {
    label: 'CYBER',
    fullLabel: 'Cybersecurity',
    color: '#10B981',
    bg: 'bg-emerald-500',
    bgLight: 'bg-emerald-50',
    border: 'border-emerald-200',
    text: 'text-emerald-600',
    badge: 'bg-emerald-100 text-emerald-700',
    jobs: ['SOC Analyst', 'Penetration Tester', 'Security Researcher', 'Security Implementer'],
  },
  product: {
    label: 'PRODUCT',
    fullLabel: 'Product & UX',
    color: '#EF4444',
    bg: 'bg-red-500',
    bgLight: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-500',
    badge: 'bg-red-100 text-red-700',
    jobs: ['Product Manager', 'UX Designer', 'UI Designer', 'Product Analyst'],
  },
  infra: {
    label: 'INFRA',
    fullLabel: 'Infrastructure & Cloud',
    color: '#8B5CF6',
    bg: 'bg-violet-500',
    bgLight: 'bg-violet-50',
    border: 'border-violet-200',
    text: 'text-violet-600',
    badge: 'bg-violet-100 text-violet-700',
    jobs: ['DevOps Engineer', 'Cloud Architect', 'SRE', 'System Administrator', 'Network Engineer'],
  },
  hardware: {
    label: 'HARDWARE',
    fullLabel: 'Hardware & Embedded',
    color: '#6B7280',
    bg: 'bg-gray-500',
    bgLight: 'bg-gray-50',
    border: 'border-gray-200',
    text: 'text-gray-600',
    badge: 'bg-gray-100 text-gray-700',
    jobs: ['Embedded Engineer', 'Firmware Developer', 'FPGA Engineer', 'Hardware Engineer', 'Electronics Engineer'],
  },
};

const CareerResultsPage = () => {
  const [searchParams] = useSearchParams();
  const barsRef = useRef([]);
  const cardRefs = useRef([]);
  const headerRef = useRef(null);

  // Parse scores from URL params — known + unknown categories
  const rawScores = {};
  searchParams.forEach((value, key) => {
    const val = parseInt(value, 10);
    if (!isNaN(val) && val > 0) rawScores[key] = val;
  });

  const total = Object.values(rawScores).reduce((s, v) => s + v, 0);
  const hasData = total > 0;

  // Sorted array of [key, pct]
  const sorted = Object.entries(rawScores)
    .map(([key, score]) => ({ key, score, pct: Math.round((score / total) * 100) }))
    .sort((a, b) => b.pct - a.pct);

  const topKey = sorted[0]?.key;

  const getCat = (key) => CATEGORIES[key] ?? {
    label: key.toUpperCase(),
    fullLabel: key.charAt(0).toUpperCase() + key.slice(1),
    color: '#64748B',
    bg: 'bg-slate-500',
    bgLight: 'bg-slate-50',
    border: 'border-slate-200',
    text: 'text-slate-600',
    badge: 'bg-slate-100 text-slate-700',
    jobs: [],
  };

  // GSAP animations
  useGSAP(() => {
    if (!hasData) return;

    // Header entrance
    gsap.from(headerRef.current, {
      y: -40,
      opacity: 0,
      duration: 0.8,
      ease: 'power3.out',
    });

    // Cards stagger
    if (cardRefs.current.length) {
      gsap.from(cardRefs.current, {
        y: 50,
        opacity: 0,
        duration: 0.6,
        stagger: 0.12,
        ease: 'power2.out',
        delay: 0.3,
      });
    }

    // Progress bars animate width from 0
    barsRef.current.forEach((bar, i) => {
      if (!bar) return;
      const target = bar.dataset.pct;
      gsap.fromTo(
        bar,
        { width: '0%' },
        { width: `${target}%`, duration: 1, ease: 'power2.out', delay: 0.5 + i * 0.1 }
      );
    });
  }, [hasData]);

  if (!hasData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex flex-col items-center justify-center px-4 text-center">
        <div className="text-6xl mb-6">🔍</div>
        <h1 className="text-2xl font-bold text-gray-800 mb-3">Results not available</h1>
        <p className="text-gray-500 mb-8">Take the assessment to discover your tech profile.</p>
        <a
          href="https://tally.so"
          target="_blank"
          rel="noopener noreferrer"
          className="px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-500 text-white font-semibold rounded-full hover:opacity-90 transition-opacity"
        >
          Take the test →
        </a>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50">
      {/* Header */}
      <div ref={headerRef} className="text-center py-14 px-4">
        <span className="inline-block bg-purple-100 text-purple-700 text-xs font-semibold px-4 py-1.5 rounded-full mb-5 tracking-wide uppercase">
          Career Assessment
        </span>
        <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-3">
          Your Tech Profile
        </h1>
        <p className="text-gray-500 text-lg max-w-xl mx-auto">
          Based on your answers, here are the careers that match you
        </p>
      </div>

      <div className="max-w-3xl mx-auto px-4 pb-20">

        {/* ── TOP PROFILE CARD ── */}
        {topKey && (() => {
          const cat = getCat(topKey);
          const topItem = sorted[0];
          return (
            <div
              ref={el => { cardRefs.current[0] = el; }}
              className={`rounded-3xl p-8 mb-8 border-2 ${cat.border} ${cat.bgLight} shadow-lg`}
            >
              <div className="mb-4">
                <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${cat.badge}`}>
                  ⭐ Your main profile
                </span>
              </div>
              <div className="flex items-end gap-4 mb-4">
                <span className={`text-6xl font-black ${cat.text}`}>{topItem.pct}%</span>
                <div className="mb-2">
                  <div className={`text-xs font-bold uppercase tracking-widest ${cat.text} mb-0.5`}>{cat.label}</div>
                  <div className="text-2xl font-bold text-gray-800">{cat.fullLabel}</div>
                </div>
              </div>
              {/* Bar */}
              <div className="h-3 bg-white/70 rounded-full overflow-hidden mb-5">
                <div
                  ref={el => { barsRef.current[0] = el; }}
                  data-pct={topItem.pct}
                  className="h-full rounded-full"
                  style={{ backgroundColor: cat.color, width: '0%' }}
                />
              </div>
              <div className="flex flex-wrap gap-2">
                {cat.jobs.map(job => (
                  <span key={job} className={`text-sm px-3 py-1 rounded-lg font-medium ${cat.badge}`}>
                    {job}
                  </span>
                ))}
              </div>
            </div>
          );
        })()}

        {/* ── OTHER CATEGORIES ── */}
        <div className="space-y-4 mb-12">
          {sorted.slice(1).map(({ key, pct }, i) => {
            const cat = getCat(key);
            return (
              <div
                key={key}
                ref={el => { cardRefs.current[i + 1] = el; }}
                className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
              >
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <span className={`text-xs font-bold uppercase tracking-widest ${cat.text}`}>{cat.label}</span>
                    <p className="text-gray-700 font-semibold">{cat.fullLabel}</p>
                  </div>
                  <span className={`text-2xl font-black ${cat.text}`}>{pct}%</span>
                </div>
                {/* Bar */}
                <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden mb-4">
                  <div
                    ref={el => { barsRef.current[i + 1] = el; }}
                    data-pct={pct}
                    className="h-full rounded-full"
                    style={{ backgroundColor: cat.color, width: '0%' }}
                  />
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {cat.jobs.map(job => (
                    <span key={job} className="text-xs px-2.5 py-1 bg-gray-50 text-gray-600 rounded-lg border border-gray-100">
                      {job}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* ── CTA ── */}
        <div
          ref={el => { cardRefs.current[sorted.length] = el; }}
          className="bg-gradient-to-r from-purple-600 to-pink-500 rounded-3xl p-10 text-center text-white shadow-xl"
        >
          <p className="text-white/70 text-sm mb-2 uppercase tracking-wider font-semibold">Next step</p>
          <h2 className="text-2xl font-bold mb-3">Want to learn more about these careers?</h2>
          <p className="text-white/70 mb-8">Explore our full program and take the leap into the right field.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/"
              className="px-8 py-3 bg-white text-purple-700 font-bold rounded-full hover:bg-purple-50 transition-colors shadow"
            >
              Explore our services →
            </Link>
            <Link
              to="/course"
              className="px-8 py-3 bg-white/15 border border-white/30 text-white font-semibold rounded-full hover:bg-white/25 transition-colors"
            >
              View the course
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CareerResultsPage;
